import request from '../utils/request';
import { Result } from '../models/general';

// Wire types for the unified Pending Actions aggregator (GET api/PendingActions/My/*).
// Hand-written on purpose: src/models/* is auto-generated and must not be edited.
// The backend serializes camelCase and OMITS null properties entirely, so every
// nullable field must tolerate an absent key. Dictionary KEYS keep their C# casing
// ('ForYou', 'Leave', 'ShiftSwapAcceptance', ...).

export type PendingActionGroup = 'ForYou' | 'Approvals';

export type PendingActionSource =
  | 'Leave'
  | 'ShiftSwap'
  | 'Claim'
  | 'AttendanceCorrection'
  | 'ShortLeave'
  | 'Encashment'
  | 'AssetRequest'
  | 'ShiftSwapAcceptance'
  | 'Onboarding'
  | 'Terms'
  | 'Survey'
  | 'ReturnToWork'
  | 'Referee'
  | 'OutOfBoundsClock'
  // Approval-wave additions. These strings are the SAME values the backend puts in
  // Notification.actionSource, so the bell hydrates a live item by `${source}:${id}`.
  // Approvals group:
  | 'InternalMovement'
  | 'EmployeeMovement'
  | 'PublicProfile'
  | 'GpssaPending'
  | 'GoalBlocker'
  | 'ReflectionResponse'
  // CO-583 — an overtime approval request. The ENTITY ID IS THE SHIFT ID, which is also
  // what Notification.actionEntityId carries, so the bell's
  // `${actionSource}:${actionEntityId}` hydrate resolves to the same live item.
  | 'OvertimeApproval'
  // Hourly pay (docs/hourly-pay-spec.md §9) — one hourly shift awaiting a decision. The ENTITY ID IS
  // ShiftPayApproval.Id, which is also Notification.actionEntityId (one shared server-side mapper).
  | 'ShiftPayApproval'
  // ForYou group:
  | 'Offboarding'
  | 'PlatformTerms'
  | 'Reflection'
  | 'PerformanceSnapshot'
  | 'RestrictedHoliday';

export type PendingActionStyle = 'Decision2' | 'Accept2' | 'Complete1' | 'OpenOnly';

export interface PendingActionItem {
  group: PendingActionGroup;
  source: PendingActionSource;
  /** Stringified domain entity id (parse per source before dispatching). */
  id: string;
  title: string;
  subtitle: string;
  actorUserId?: string;
  actorName?: string;
  actorImageUrl?: string;
  primaryDate?: string;
  submittedOn: string;
  status: string;
  reason?: string;
  blocking: boolean;
  actionStyle: PendingActionStyle;
  meta?: Record<string, string>;
}

export interface PendingActionsView {
  items: PendingActionItem[];
  /** Only groups with count > 0 are present — default absent keys to 0. */
  countsByGroup: Partial<Record<PendingActionGroup, number>>;
  /** Only sources with count > 0 are present — default absent keys to 0. */
  countsByType: Partial<Record<PendingActionSource, number>>;
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface PendingActionsQuery {
  /** Comma list of groups ('ForYou,Approvals'), case-insensitive server-side. */
  groups?: string;
  /** Comma list of Source keys, case-insensitive server-side. */
  types?: string;
  page?: number;
  pageSize?: number;
}

export async function getPendingActions(params?: PendingActionsQuery): Promise<Result<PendingActionsView>> {
  const response = await request.get<Result<PendingActionsView>>('PendingActions/My/Items', { params });
  return response.data;
}

export async function getPendingActionsCount(): Promise<Result<number>> {
  const response = await request.get<Result<number>>('PendingActions/My/Count');
  return response.data;
}
